import { IconButton, Menu } from "@controls";
import type { MenuGroup } from "@controls";
import {
  DndContext,
  DragEndEvent,
  KeyboardCode,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type {
  CollisionDetection,
  DraggableAttributes,
  useDraggable,
} from "@dnd-kit/core";
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ArrowDown, ArrowUp, EllipsisVertical } from "@icons";
import { useReorderAnnouncements } from "@src/hooks/use-reorder-announcements";
import { useReorderLabels } from "@src/hooks/use-reorder-labels";
import {
  ReorderAxis,
  moveWithin,
  reorderCoordinateGetter,
} from "@src/utils/reorder";
import { ReorderKeyboardSensor } from "@src/utils/reorder-keyboard-sensor";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface ReorderChange {
  activeId: string;
  overId: string;
}

interface ReorderList {
  ids: string[];
  saving: boolean;
  moveBy: (id: string, delta: number) => void;
}

interface PendingRelease {
  startKey: string;
  committedKey: string;
  timer: ReturnType<typeof setTimeout>;
}

const KEYBOARD_CODES = {
  start: [KeyboardCode.Space, KeyboardCode.Enter],
  cancel: [KeyboardCode.Esc, KeyboardCode.Tab],
  end: [KeyboardCode.Space, KeyboardCode.Enter],
};
const HORIZONTAL_MODIFIERS = [restrictToHorizontalAxis];
const VERTICAL_MODIFIERS = [restrictToVerticalAxis];
const KEY_SEPARATOR = "\u0000";
const COMMIT_GRACE_MS = 1500;

const INERT_LIST: ReorderList = {
  ids: [],
  saving: false,
  moveBy: () => undefined,
};

const ReorderListContext = createContext<ReorderList | null>(null);

const isThenable = (value: unknown): value is PromiseLike<unknown> =>
  typeof (value as { then?: unknown } | undefined)?.then === "function";

interface ReorderContextProps {
  axis: ReorderAxis;
  ids: string[];
  getName: (id: string) => string;
  onReorder: (
    orderedIds: string[],
    change: ReorderChange,
  ) => void | PromiseLike<unknown>;
  pointerDistance?: number;
  sortable?: boolean;
  collisionDetection?: CollisionDetection;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onDragCancel?: () => void;
  children: ReactNode;
}

export function ReorderContext({
  axis,
  ids,
  getName,
  onReorder,
  pointerDistance,
  sortable = false,
  collisionDetection = closestCenter,
  onDragStart,
  onDragEnd,
  onDragCancel,
  children,
}: ReorderContextProps) {
  const {
    announcements,
    screenReaderInstructions,
    labels,
    announceMove,
    announceSaved,
    announceSaveFailed,
  } = useReorderAnnouncements({ ids, getName });

  const idsKey = ids.join(KEY_SEPARATOR);
  const stableIds = useMemo(
    () => (idsKey === "" ? [] : idsKey.split(KEY_SEPARATOR)),
    [idsKey],
  );
  const pointerOptions = useMemo(
    () =>
      pointerDistance
        ? { activationConstraint: { distance: pointerDistance } }
        : undefined,
    [pointerDistance],
  );
  const coordinateGetter = useMemo(() => reorderCoordinateGetter(axis), [axis]);
  const keyboardOptions = useMemo(
    () => ({ keyboardCodes: KEYBOARD_CODES, coordinateGetter }),
    [coordinateGetter],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, pointerOptions),
    useSensor(ReorderKeyboardSensor, keyboardOptions),
  );

  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const mounted = useRef(true);
  const pendingRelease = useRef<PendingRelease | null>(null);
  const idsKeyRef = useRef(idsKey);
  idsKeyRef.current = idsKey;
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const releaseLock = useCallback(
    (announce: boolean) => {
      if (pendingRelease.current) clearTimeout(pendingRelease.current.timer);
      pendingRelease.current = null;
      savingRef.current = false;
      if (!mounted.current) return;
      setSaving(false);
      if (announce) announceSaved();
    },
    [announceSaved],
  );

  const releaseOnTimeout = useCallback(() => releaseLock(false), [releaseLock]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (pendingRelease.current) clearTimeout(pendingRelease.current.timer);
    };
  }, []);

  useEffect(() => {
    const pending = pendingRelease.current;
    if (!pending) return;
    if (idsKey === pending.committedKey) releaseLock(true);
    else if (idsKey !== pending.startKey) releaseLock(false);
  }, [idsKey, releaseLock]);

  const commit = useCallback(
    (orderedIds: string[], change: ReorderChange, movedText = "") => {
      const result = onReorder(orderedIds, change);
      if (!isThenable(result)) {
        if (movedText) announceMove(movedText, false);
        return;
      }
      const startKey = idsKeyRef.current;
      const committedKey = orderedIds.join(KEY_SEPARATOR);
      savingRef.current = true;
      setSaving(true);
      announceMove(movedText, true);
      result.then(
        () => {
          if (!mounted.current) return;
          if (idsKeyRef.current === committedKey) {
            releaseLock(true);
            return;
          }
          if (idsKeyRef.current !== startKey) {
            releaseLock(false);
            return;
          }
          pendingRelease.current = {
            startKey,
            committedKey,
            timer: setTimeout(releaseOnTimeout, COMMIT_GRACE_MS),
          };
        },
        () => {
          savingRef.current = false;
          if (!mounted.current) return;
          setSaving(false);
          const committed = idsRef.current;
          announceSaveFailed(
            getName(change.activeId),
            committed.indexOf(change.activeId) + 1,
            committed.length,
          );
        },
      );
    },
    [
      announceMove,
      announceSaveFailed,
      getName,
      onReorder,
      releaseLock,
      releaseOnTimeout,
    ],
  );

  const moveBy = useCallback(
    (id: string, delta: number) => {
      if (savingRef.current) return;
      const move = moveWithin(stableIds, id, delta);
      if (!move) return;
      commit(
        move.ordered,
        { activeId: id, overId: move.overId },
        labels.moved(
          getName(id),
          stableIds.indexOf(move.overId) + 1,
          stableIds.length,
        ),
      );
    },
    [commit, getName, labels, stableIds],
  );

  const handleDragStart = useCallback(() => {
    onDragStart?.();
  }, [onDragStart]);

  const handleDragCancel = useCallback(() => {
    onDragCancel?.();
  }, [onDragCancel]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      onDragEnd?.();
      const { active, over } = event;
      if (savingRef.current || !over || active.id === over.id) return;
      const activeId = String(active.id);
      const overId = String(over.id);
      const move = moveWithin(
        stableIds,
        activeId,
        stableIds.indexOf(overId) - stableIds.indexOf(activeId),
      );
      if (move) commit(move.ordered, { activeId, overId });
    },
    [commit, onDragEnd, stableIds],
  );

  const value = useMemo<ReorderList>(
    () => ({ ids: stableIds, saving, moveBy }),
    [stableIds, saving, moveBy],
  );

  return (
    <ReorderListContext.Provider value={value}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        modifiers={
          axis === "horizontal" ? HORIZONTAL_MODIFIERS : VERTICAL_MODIFIERS
        }
        accessibility={{
          announcements,
          screenReaderInstructions,
          container: document.body,
        }}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        {sortable ? (
          <SortableContext
            items={stableIds}
            strategy={verticalListSortingStrategy}
          >
            {children}
          </SortableContext>
        ) : (
          children
        )}
      </DndContext>
    </ReorderListContext.Provider>
  );
}

export function useReorderList() {
  const list = useContext(ReorderListContext) ?? INERT_LIST;
  return {
    ids: list.ids,
    saving: list.saving,
    moveBy: list.moveBy,
    positionOf: (id: string) => list.ids.indexOf(id) + 1,
  };
}

type DraggableListeners = ReturnType<typeof useDraggable>["listeners"];

export function useReorderItem(id: string) {
  const list = useContext(ReorderListContext) ?? INERT_LIST;
  const labels = useReorderLabels();

  const total = list.ids.length;
  const position = list.ids.indexOf(id) + 1;
  const disabled = list.saving;

  const moveBy = (delta: number) => list.moveBy(id, delta);

  const moveGroup: MenuGroup | null =
    total < 2 || position === 0
      ? null
      : {
          key: "move",
          items: [
            {
              key: "move-up",
              icon: <ArrowUp />,
              label: labels.moveUp,
              disabled: disabled || position === 1,
              onSelect: () => moveBy(-1),
            },
            {
              key: "move-down",
              icon: <ArrowDown />,
              label: labels.moveDown,
              disabled: disabled || position === total,
              onSelect: () => moveBy(1),
            },
          ],
        };

  const handleProps = (
    attributes: DraggableAttributes,
    listeners: DraggableListeners,
    label: string,
  ) => ({
    ...listeners,
    "aria-label": label,
    "aria-describedby": attributes["aria-describedby"],
    "aria-roledescription": attributes["aria-roledescription"],
    "aria-disabled": attributes["aria-disabled"],
  });

  const names = {
    column: (name: string) => labels.handleColumn(name, position, total),
    lineItem: (name: string) => labels.handleLineItem(name, position, total),
    discount: (name: string) => labels.handleDiscount(name, position, total),
    rowActions: (name: string) => labels.rowActionsName(name, position, total),
  };

  return {
    labels,
    names,
    position,
    total,
    disabled,
    roleDescription: labels.roleDescription,
    moveGroup,
    moveBy,
    handleProps,
  };
}

interface RowMenuProps {
  moveGroup: MenuGroup | null;
  label: string;
}

export function RowMenu({ moveGroup, label }: RowMenuProps) {
  const labels = useReorderLabels();
  if (!moveGroup) return null;
  return (
    <Menu
      ariaLabel={label}
      align="end"
      trigger={
        <IconButton
          size="sm"
          variant="ghost"
          icon={<EllipsisVertical />}
          label={label}
          tipLabel={labels.rowActionsTip}
        />
      }
      groups={[moveGroup]}
    />
  );
}
